import { Component } from 'react';
import { CssGeneratorLayout, cssGenCommon as genStyles } from '../converter-layout/css-generator-layout';

type CardType = 'summary' | 'summary_large_image' | 'app' | 'player';

interface State {
    cardType: CardType;
    site: string;
    creator: string;
    title: string;
    description: string;
    image: string;
    imageAlt: string;
    playerUrl: string;
    playerWidth: string;
    playerHeight: string;
    iosAppId: string;
    iosAppName: string;
    androidAppId: string;
    androidAppName: string;
    copied: boolean;
}

const DEFAULT: State = {
    cardType: 'summary_large_image',
    site: '',
    creator: '',
    title: '',
    description: '',
    image: '',
    imageAlt: '',
    playerUrl: '',
    playerWidth: '480',
    playerHeight: '320',
    iosAppId: '',
    iosAppName: '',
    androidAppId: '',
    androidAppName: '',
    copied: false,
};

export default class TwitterCardGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = { ...DEFAULT };

    private esc(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private generate(): string {
        const s = this.state;
        const out: string[] = [`<meta name="twitter:card" content="${s.cardType}">`];
        if (s.site) out.push(`<meta name="twitter:site" content="${this.esc(s.site)}">`);
        if (s.creator) out.push(`<meta name="twitter:creator" content="${this.esc(s.creator)}">`);
        if (s.title) out.push(`<meta name="twitter:title" content="${this.esc(s.title)}">`);
        if (s.description) out.push(`<meta name="twitter:description" content="${this.esc(s.description)}">`);
        if (['summary', 'summary_large_image', 'player'].includes(s.cardType)) {
            if (s.image) out.push(`<meta name="twitter:image" content="${this.esc(s.image)}">`);
            if (s.image && s.imageAlt) out.push(`<meta name="twitter:image:alt" content="${this.esc(s.imageAlt)}">`);
        }
        if (s.cardType === 'player') {
            if (s.playerUrl) out.push(`<meta name="twitter:player" content="${this.esc(s.playerUrl)}">`);
            if (s.playerWidth) out.push(`<meta name="twitter:player:width" content="${this.esc(s.playerWidth)}">`);
            if (s.playerHeight) out.push(`<meta name="twitter:player:height" content="${this.esc(s.playerHeight)}">`);
        }
        if (s.cardType === 'app') {
            if (s.iosAppId) out.push(`<meta name="twitter:app:id:iphone" content="${this.esc(s.iosAppId)}">`);
            if (s.iosAppName) out.push(`<meta name="twitter:app:name:iphone" content="${this.esc(s.iosAppName)}">`);
            if (s.androidAppId) out.push(`<meta name="twitter:app:id:googleplay" content="${this.esc(s.androidAppId)}">`);
            if (s.androidAppName) out.push(`<meta name="twitter:app:name:googleplay" content="${this.esc(s.androidAppName)}">`);
        }
        return out.join('\n');
    }

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private set(key: keyof State, val: string) {
        this.setState({ [key]: val } as any);
    }

    render() {
        const s = this.state;
        const output = this.generate();

        const controls = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Card</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="tw-type">Card type</label>
                        <select id="tw-type" value={s.cardType} onChange={e => this.set('cardType', e.target.value)}>
                            <option value="summary">summary</option>
                            <option value="summary_large_image">summary_large_image</option>
                            <option value="app">app</option>
                            <option value="player">player</option>
                        </select>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-site">@site</label>
                            <input id="tw-site" type="text" value={s.site} placeholder="@example"
                                onChange={e => this.set('site', e.target.value)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-creator">@creator (optional)</label>
                            <input id="tw-creator" type="text" value={s.creator} placeholder="@author"
                                onChange={e => this.set('creator', e.target.value)} />
                        </div>
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Content</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="tw-title">Title</label>
                        <input id="tw-title" type="text" value={s.title} placeholder="Page title (max 70 chars)"
                            onChange={e => this.set('title', e.target.value)} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="tw-desc">Description</label>
                        <textarea id="tw-desc" rows={2} value={s.description} placeholder="Up to 200 characters"
                            onChange={e => this.set('description', e.target.value)} />
                    </div>
                </div>
                {['summary', 'summary_large_image', 'player'].includes(s.cardType) && (
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Image</span></div>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-img">Image URL</label>
                            <input id="tw-img" type="url" value={s.image} placeholder="https://example.com/card.png"
                                onChange={e => this.set('image', e.target.value)} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-alt">Image alt text</label>
                            <input id="tw-alt" type="text" value={s.imageAlt}
                                onChange={e => this.set('imageAlt', e.target.value)} />
                        </div>
                    </div>
                )}
                {s.cardType === 'player' && (
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Player</span></div>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-player">Player iframe URL</label>
                            <input id="tw-player" type="url" value={s.playerUrl}
                                onChange={e => this.set('playerUrl', e.target.value)} />
                        </div>
                        <div className={genStyles.fieldRow}>
                            <div className={genStyles.field}>
                                <label htmlFor="tw-w">Width</label>
                                <input id="tw-w" type="number" inputMode="decimal" value={s.playerWidth}
                                    onChange={e => this.set('playerWidth', e.target.value)} />
                            </div>
                            <div className={genStyles.field}>
                                <label htmlFor="tw-h">Height</label>
                                <input id="tw-h" type="number" inputMode="decimal" value={s.playerHeight}
                                    onChange={e => this.set('playerHeight', e.target.value)} />
                            </div>
                        </div>
                    </div>
                )}
                {s.cardType === 'app' && (
                    <div className={genStyles.section}>
                        <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>App IDs</span></div>
                        <div className={genStyles.field}><label htmlFor="ios-id">iOS App ID</label><input id="ios-id" type="text" value={s.iosAppId} onChange={e => this.set('iosAppId', e.target.value)} /></div>
                        <div className={genStyles.field}><label htmlFor="ios-name">iOS App Name</label><input id="ios-name" type="text" value={s.iosAppName} onChange={e => this.set('iosAppName', e.target.value)} /></div>
                        <div className={genStyles.field}><label htmlFor="and-id">Android Package ID</label><input id="and-id" type="text" value={s.androidAppId} onChange={e => this.set('androidAppId', e.target.value)} /></div>
                        <div className={genStyles.field}><label htmlFor="and-name">Android App Name</label><input id="and-name" type="text" value={s.androidAppName} onChange={e => this.set('androidAppName', e.target.value)} /></div>
                    </div>
                )}
            </>
        );

        const isLarge = s.cardType === 'summary_large_image';
        const preview = (s.title || s.image) ? (
            <div style={{ width: '100%', maxWidth: 380, background: '#fff', border: '1px solid #cfd9de', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }} role="img" aria-label="Twitter card preview">
                {s.image && <img src={s.image} alt={s.imageAlt} style={{ width: '100%', height: isLarge ? 190 : 96, objectFit: 'cover' }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />}
                <div style={{ padding: '0.65rem 0.85rem' }}>
                    {s.site && <div style={{ fontSize: '0.78rem', color: '#536471' }}>{s.site.replace(/^@/, '')}</div>}
                    {s.title && <div style={{ fontWeight: 600, color: '#0f1419', marginTop: 2 }}>{s.title}</div>}
                    {s.description && <div style={{ fontSize: '0.85rem', color: '#536471', marginTop: 4 }}>{s.description.length > 120 ? s.description.slice(0, 120) + '…' : s.description}</div>}
                </div>
            </div>
        ) : (
            <div style={{ color: '#64748b', textAlign: 'center' }}>
                <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>𝕏</div>
                <div style={{ marginTop: '0.5rem' }}>Fill in title or image to see the preview</div>
            </div>
        );

        return (
            <CssGeneratorLayout
                controls={controls}
                previewLabel="Card preview"
                previewMeta={s.cardType}
                preview={preview}
                codeLabel="Generated meta tags"
                codeMeta={`${output.split('\n').filter(Boolean).length} tags`}
                code={output}
                codeActions={
                    <button className={`${genStyles.iconBtn} ${s.copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}>{s.copied ? '✓ Copied' : '📋 Copy'}</button>
                }
            />
        );
    }
}
